import { QueryKeys } from "@/constants/Query";
import type { RawVisitDiagnosesResponse } from "@/features/Charting/network/useVisitDiagnoses";
import { withMultipleEncountersHandler } from "@/features/Patient/MedicalRecords/MedicalHistory/useMedicalHistoryErrorHandler";
import type { ApiError } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import type { Condition, Encounter } from "fhir/r4";

export interface ReorderDiagnosisInVisitParams {
  conditionId: string;
  position: number;
}

interface ReorderDiagnosisInVisitResponse {
  encounter: Encounter;
}

export const useReorderDiagnosisInVisit = ({
  patientId,
  motherEncounterId,
  onSuccess,
  onError,
}: {
  patientId: string;
  motherEncounterId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  const visitDiagnosesQueryKey =
    QueryKeys.Patient.Chart.Conditions.VisitDiagnoses({
      patientId,
      encounterId: motherEncounterId,
    });

  return useApiMutation<
    ReorderDiagnosisInVisitResponse,
    ReorderDiagnosisInVisitParams
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Conditions.REORDER_DIAGNOSIS({
        patientId,
        encounterId: motherEncounterId,
      }),
      mutationFn: ({ conditionId, position }) =>
        axiosInstance.post(
          `/charting/encounter-charting/${motherEncounterId}/diagnosis/reorder`,
          { conditionId, position },
        ),
      onMutate: async ({ conditionId, position }) => {
        await queryClient.cancelQueries({ queryKey: visitDiagnosesQueryKey });
        const previousResponse =
          queryClient.getQueryData<RawVisitDiagnosesResponse>(
            visitDiagnosesQueryKey,
          );

        if (previousResponse) {
          queryClient.setQueryData<RawVisitDiagnosesResponse>(
            visitDiagnosesQueryKey,
            {
              ...previousResponse,
              visitDiagnoses: reorderConditions(
                previousResponse.visitDiagnoses,
                conditionId,
                position,
              ),
            },
          );
        }

        return { previousResponse };
      },
      onError: (error, _variables, context) => {
        const mutationContext = context as
          | { previousResponse?: RawVisitDiagnosesResponse }
          | undefined;
        if (mutationContext?.previousResponse) {
          queryClient.setQueryData(
            visitDiagnosesQueryKey,
            mutationContext.previousResponse,
          );
        }

        withMultipleEncountersHandler(onError)(error);
      },
      onSuccess,
      onSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: visitDiagnosesQueryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Billing.ENCOUNTER_CODING({
            patientId,
            encounterId: motherEncounterId,
          }),
        });
        void queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.Chart.VisitClosure.RELEVANT_DIAGNOSIS_HISTORY({
              patientId,
              encounterId: motherEncounterId,
            }),
        });
        // The timeline titles a single-encounter visit from its diagnoses in rank order.
        void queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTERS({
            patientId,
          }),
        });
        // The timeline's Diagnoses filter orders options charted on the same visit by rank.
        void queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.EpisodesOfCare.LIST(patientId),
        });
      },
    },
  });
};

const reorderConditions = (
  conditions: Condition[],
  conditionId: string,
  position: number,
) => {
  const currentIndex = conditions.findIndex(
    (condition) => condition.id === conditionId,
  );
  if (currentIndex < 0) {
    return conditions;
  }

  const nextConditions = [...conditions];
  const [condition] = nextConditions.splice(currentIndex, 1);
  nextConditions.splice(
    Math.max(0, Math.min(position - 1, nextConditions.length)),
    0,
    condition,
  );
  return nextConditions;
};
